import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import { buildReviewHierarchy } from "./dtoolsStagingTransform";

/** Clamp quantity and unit price bounds; preserve authoritative line.lineTotal (InstallPrice). */
export function sanitizeStagingLineFields<T extends DtoolsStagingLine>(lines: T[]): T[] {
  return lines.map((line) => {
    const quantity = Math.max(1, Math.round(line.quantity));
    const unitPrice = Math.max(0, line.unitPrice);
    const lineTotal = Math.max(0, Number(line.lineTotal) || 0);
    if (line.quantity === quantity && line.unitPrice === unitPrice && line.lineTotal === lineTotal) {
      return line;
    }
    return { ...line, quantity, unitPrice, lineTotal };
  });
}

/** @deprecated Use sanitizeStagingLineFields — lineTotal is authoritative and must not be overwritten. */
export function normalizeStagingLineTotals<T extends DtoolsStagingLine>(lines: T[]): T[] {
  return sanitizeStagingLineFields(lines);
}

function isExplicitlyDemotedAsAccessory(
  line: DtoolsStagingLine,
  overrides?: DtoolsHierarchyOverrides | null,
): boolean {
  const map = overrides?.parentByLineId ?? {};
  for (const key of [line.id, line.dtoolsItemId].filter(Boolean)) {
    if (key in map) {
      const parent = map[key];
      return parent != null && parent !== "";
    }
  }
  return false;
}

/**
 * Service and cable lines should render as room primaries unless staff explicitly demoted them.
 * Fixes imported D-Tools parent links that incorrectly nest labour under equipment.
 *
 * Note: quotation PDF uses {@link elevateServiceLinesOnlyForQuotationPdf} so cables with a
 * valid parentId remain nested supporting components (e.g. HP142 under a projector).
 */
export function elevateServiceLinesToRoomPrimaries<T extends DtoolsStagingLine>(
  lines: T[],
  overrides?: DtoolsHierarchyOverrides | null,
): T[] {
  return lines.map((line) => {
    if (line.bucket !== "service" && line.bucket !== "cable") return line;
    if (isExplicitlyDemotedAsAccessory(line, overrides)) return line;
    if (!line.parentId && !line.isAccessory) return line;
    return { ...line, parentId: null, isAccessory: false };
  });
}

/**
 * Quotation PDF hierarchy: elevate labour/service lines only.
 * Cables keep D-Tools parentId so they nest as supporting components under primaries.
 */
export function elevateServiceLinesOnlyForQuotationPdf<T extends DtoolsStagingLine>(
  lines: T[],
  overrides?: DtoolsHierarchyOverrides | null,
): T[] {
  return lines.map((line) => {
    if (line.bucket !== "service") return line;
    if (isExplicitlyDemotedAsAccessory(line, overrides)) return line;
    if (!line.parentId && !line.isAccessory) return line;
    return { ...line, parentId: null, isAccessory: false };
  });
}

export type DtoolsHierarchyOverrides = {
  /** Proposal-specific parent assignment by line id (null = promoted to primary). */
  parentByLineId: Record<string, string | null>;
};

export const EMPTY_DTOOLS_HIERARCHY_OVERRIDES: DtoolsHierarchyOverrides = { parentByLineId: {} };

export function pruneDtoolsHierarchyOverridesForLines(
  overrides: DtoolsHierarchyOverrides | null | undefined,
  lines: DtoolsStagingLine[],
): DtoolsHierarchyOverrides {
  const validIds = new Set(
    lines.flatMap((line) => [line.id, line.dtoolsItemId].filter(Boolean) as string[]),
  );
  const parentByLineId = Object.fromEntries(
    Object.entries(overrides?.parentByLineId ?? {}).filter(
      ([lineId, parentId]) =>
        validIds.has(lineId) && (parentId == null || validIds.has(parentId)),
    ),
  );
  return { parentByLineId };
}

export function readDtoolsHierarchyOverrides(parsedMetadataJson: unknown): DtoolsHierarchyOverrides {
  if (!parsedMetadataJson || typeof parsedMetadataJson !== "object" || Array.isArray(parsedMetadataJson)) {
    return { ...EMPTY_DTOOLS_HIERARCHY_OVERRIDES };
  }
  const raw = (parsedMetadataJson as Record<string, unknown>).dtoolsHierarchyOverrides;
  const snapshot = raw
    ? null
    : (parsedMetadataJson as Record<string, unknown>).dtoolsHierarchySnapshot;
  const snapshotOverrides =
    snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)
      ? (snapshot as Record<string, unknown>).hierarchyOverrides
      : null;
  const source = raw ?? snapshotOverrides;
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return { ...EMPTY_DTOOLS_HIERARCHY_OVERRIDES };
  }
  const parentByLineId: Record<string, string | null> = {};
  const map = (source as Record<string, unknown>).parentByLineId;
  if (map && typeof map === "object" && !Array.isArray(map)) {
    for (const [key, value] of Object.entries(map)) {
      if (value === null) parentByLineId[key] = null;
      else if (typeof value === "string" && value.trim()) parentByLineId[key] = value.trim();
    }
  }
  return { parentByLineId };
}

export function mergeDtoolsHierarchyOverrides(
  parsedMetadataJson: unknown,
  overrides: DtoolsHierarchyOverrides,
): Record<string, unknown> {
  const base =
    parsedMetadataJson && typeof parsedMetadataJson === "object" && !Array.isArray(parsedMetadataJson)
      ? { ...(parsedMetadataJson as Record<string, unknown>) }
      : {};
  return {
    ...base,
    dtoolsHierarchyOverrides: overrides,
  };
}

function lineIdsMatch(a: string, b: string): boolean {
  return a === b || a.trim() === b.trim();
}

function resolveParentId(line: DtoolsStagingLine, overrides: DtoolsHierarchyOverrides): string | null {
  const keys = [line.id, line.dtoolsItemId].filter(Boolean) as string[];
  for (const key of keys) {
    if (key in overrides.parentByLineId) {
      return overrides.parentByLineId[key] ?? null;
    }
  }
  return line.parentId ?? null;
}

/** Apply saved proposal hierarchy overrides before building review hierarchy. */
export function applyHierarchyOverridesToLines(
  lines: DtoolsStagingLine[],
  overrides?: DtoolsHierarchyOverrides | null,
): DtoolsStagingLine[] {
  const map = overrides?.parentByLineId ?? {};
  if (Object.keys(map).length === 0) return lines;

  return lines.map((line) => {
    const keys = [line.id, line.dtoolsItemId].filter(Boolean) as string[];
    const hasOverride = keys.some((key) => key in map);
    if (!hasOverride) return line;

    const parentId = resolveParentId(line, overrides!);
    return {
      ...line,
      parentId,
      isAccessory: parentId != null,
    };
  });
}

function normalizeGroup(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function sameSystemRoom(a: DtoolsStagingLine, b: DtoolsStagingLine): boolean {
  return normalizeGroup(a.systemName) === normalizeGroup(b.systemName) && normalizeGroup(a.roomName) === normalizeGroup(b.roomName);
}

/** Ordered primary line ids in the same system/room as the target (for demote parent lookup). */
export function orderedPrimaryLineIdsInRoom(lines: DtoolsStagingLine[], targetLineId: string): string[] {
  const target = lines.find((l) => lineIdsMatch(l.id, targetLineId));
  if (!target) return [];

  const roomLines = lines
    .filter((l) => sameSystemRoom(l, target))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id.localeCompare(b.id));

  const { systems } = buildReviewHierarchy(applyHierarchyOverridesToLines(roomLines, { parentByLineId: {} }));
  const system = systems.find((s) => normalizeGroup(s.name) === normalizeGroup(target.systemName));
  if (!system) return [];

  const room = system.rooms.find((r) => normalizeGroup(r.name) === normalizeGroup(target.roomName));
  const blocks = room?.primaries ?? system.systemLevelPrimaries;
  return blocks.map((b) => b.line.id);
}

export function canDemoteDtoolsLine(lines: DtoolsStagingLine[], lineId: string, overrides: DtoolsHierarchyOverrides): string | null {
  const applied = applyHierarchyOverridesToLines(lines, overrides);
  const target = applied.find((l) => lineIdsMatch(l.id, lineId));
  if (!target || target.parentId) return null;

  const ordered = orderedPrimaryLineIdsInRoom(applied, lineId);
  const idx = ordered.findIndex((id) => lineIdsMatch(id, lineId));
  if (idx <= 0) return null;
  return ordered[idx - 1] ?? null;
}

export function promoteDtoolsHierarchyLine(
  lines: DtoolsStagingLine[],
  lineId: string,
  overrides: DtoolsHierarchyOverrides,
): { lines: DtoolsStagingLine[]; overrides: DtoolsHierarchyOverrides } {
  const target = lines.find((l) => lineIdsMatch(l.id, lineId));
  if (!target) return { lines, overrides };

  const nextOverrides: DtoolsHierarchyOverrides = {
    parentByLineId: { ...overrides.parentByLineId, [target.id]: null },
  };
  const nextLines = applyHierarchyOverridesToLines(lines, nextOverrides);
  return { lines: nextLines, overrides: nextOverrides };
}

export function demoteDtoolsHierarchyLine(
  lines: DtoolsStagingLine[],
  lineId: string,
  overrides: DtoolsHierarchyOverrides,
): { lines: DtoolsStagingLine[]; overrides: DtoolsHierarchyOverrides } {
  const parentId = canDemoteDtoolsLine(lines, lineId, overrides);
  if (!parentId) return { lines, overrides };

  const target = lines.find((l) => lineIdsMatch(l.id, lineId));
  if (!target) return { lines, overrides };

  const nextOverrides: DtoolsHierarchyOverrides = {
    parentByLineId: { ...overrides.parentByLineId, [target.id]: parentId },
  };
  const nextLines = applyHierarchyOverridesToLines(lines, nextOverrides);
  return { lines: nextLines, overrides: nextOverrides };
}

export function isLineSupportingInHierarchy(
  lines: DtoolsStagingLine[],
  lineId: string,
  overrides: DtoolsHierarchyOverrides,
): boolean {
  const applied = applyHierarchyOverridesToLines(lines, overrides);
  const line = applied.find((l) => lineIdsMatch(l.id, lineId));
  return Boolean(line?.parentId);
}
